/**
 * Solicitudes de aprobación (épica Aprobaciones: FC-686, FC-688, FC-690). Es el
 * ÚNICO sitio que conoce el contrato del backend, como `lostBusinessApi.js` e
 * `inboxApi.js`. Plan y decisiones: `.claude/crm_aprobaciones/plan.md`.
 *
 * Contrato: `.claude/crm_aprobaciones/aprobaciones-crm-front.md` (entregado por
 * el backend, rama `feature/aprobaciones_crm`).
 *
 * Fuera de este archivo una solicitud tiene SIEMPRE la forma «de vista»
 * (`traducirSolicitud`), en español y plana. Así las pantallas no cambian si el
 * backend renombra un campo.
 */
import { apiClient } from "@/api/softphone_api";
import { reassignScope } from "@/modules/fxCrm/helpers/reassignTargets";

/** Recurso de coordinación y administración (pendientes, resueltas, resolver). */
const BASE = "/api/fxCrm/CrmApprovalRequest/";
/** Pedir cuelga de la GESTIÓN: `InteractionManagement/<id>/request_approval/`. */
const MANAGEMENT_URL = "/api/fxCrm/InteractionManagement/";

/** Tope del descuento (FC-686 CA-03). */
export const MAX_DISCOUNT = 60;

/** Estados de una solicitud, como se leen en pantalla. */
export const ESTADOS = {
  pendiente: "Pendiente",
  aprobada: "Aprobada",
  rechazada: "Rechazada",
};

// El backend manda el estado ya en español («Pendiente», «Aprobada»,
// «Rechazada»); se normaliza a la clave de vista.
const ESTADO_DESDE_BACKEND = {
  Pendiente: "pendiente",
  Aprobada: "aprobada",
  Rechazada: "rechazada",
};

/**
 * «Quedaría en»: el valor con el descuento, redondeado a unidades, igual que el
 * mockup (`Math.round(valor × (1 − %/100))`). Solo para anticipar en el modal:
 * el congelado lo calcula el backend.
 */
export const valorConDescuento = (valor, porcentaje) => {
  const pct = Math.min(Math.max(Number(porcentaje) || 0, 0), 100);
  return Math.round((Number(valor) || 0) * (1 - pct / 100));
};

/** ¿Trae porcentaje? Sin él, la solicitud es una condición que no toca el precio. */
export const tieneDescuento = (solicitud) => Number(solicitud?.descuento) > 0;

/** Lo que se resigna: cotizado − solicitado (nunca negativo). */
export const loQueSeResigna = (solicitud) =>
  Math.max(0, (Number(solicitud?.valorCotizado) || 0) - (Number(solicitud?.valorSolicitado) || 0));

// ---------------------------------------------------------------------------
// Quién pide y a quién le llega (task_1 y FC-686, actualizadas el 2026-10-13)
// ---------------------------------------------------------------------------

/**
 * ¿Puede pedir aprobación sobre esta interacción? Solo el gestor y el
 * coordinador, y cada uno sobre SUS PROPIAS interacciones, es decir, las que
 * tiene como responsable. El administrador no pide: lo decidió el equipo para
 * evitar confusiones, porque no necesita la aprobación de nadie.
 *
 * El responsable (`user_pbx`) puede llegar como objeto o como id, según el
 * serializer; se acepta cualquiera de las dos formas.
 */
export const puedePedirAprobacion = (interaccion, userInfo) => {
  if (reassignScope(userInfo) === "admin") return false;
  const responsable = interaccion?.user_pbx;
  const responsableId = responsable && typeof responsable === "object" ? responsable.id : responsable;
  const miId = userInfo?.User_id;
  return responsableId != null && miId != null && String(responsableId) === String(miId);
};

/**
 * A quién le llega la solicitud, para los textos del modal y del botón. Si la
 * pide un gestor, la resuelven la coordinación de su grupo y la
 * administración; si la pide un coordinador, solo la administración (nadie
 * resuelve sus propias solicitudes, FC-688).
 */
export const quienResuelve = (userInfo) =>
  reassignScope(userInfo) === "coordinator" ? "administración" : "coordinación y administración";

// ---------------------------------------------------------------------------
// Bloqueo por una solicitud pendiente (task_4 y task_4_backend CA-02)
// ---------------------------------------------------------------------------

/**
 * Con una solicitud pendiente NO se puede: avanzar ni devolver el estado,
 * marcar como ganada (aunque negocio sea el último estado), cerrar como
 * perdida, descartar ni pausar. Es la regla que aplica el backend (validada por
 * el usuario el 2026-10-14), y el front la aplica ANTES, con este texto.
 */
export const MENSAJE_BLOQUEO_APROBACION =
  "Hay una solicitud de aprobación esperando respuesta: no se puede cambiar de estado, ganar, perder, pausar ni descartar hasta que la resuelvan.";

/**
 * El mensaje del backend cuando rechaza por una solicitud pendiente, o null si
 * el error es otro. Llega de dos formas:
 * - PATCH de la gestión: `{ detail, pending_approval: <id> }`;
 * - descartar o pausar: `{ itx_management: ["…solicitud de aprobación pendiente…"] }`.
 */
export function mensajeDeBloqueoPorAprobacion(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return null;
  if (data.pending_approval != null) return data.detail || MENSAJE_BLOQUEO_APROBACION;
  const delCampo = [].concat(data.itx_management || []).find(
    (m) => typeof m === "string" && /aprobaci[oó]n pendiente/i.test(m)
  );
  return delCampo || null;
}

/**
 * ¿La gestión tiene una solicitud pendiente? Pide su detalle, porque las
 * tarjetas de los tableros y las filas del listado no traen `last_approval`.
 * Lo usan los modales de descartar y pausar al abrirse, que es el punto común de
 * todos los sitios desde donde se lanzan. Si la consulta falla, devuelve false:
 * el backend es la segunda capa.
 */
export async function tieneAprobacionPendiente(managementId) {
  if (managementId == null) return false;
  try {
    const { data } = await apiClient.get(`${MANAGEMENT_URL}${managementId}/`);
    return resumenDeAprobacion(data).pendiente;
  } catch (error) {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Traducción backend → vista
// ---------------------------------------------------------------------------

const nombreDe = (persona) =>
  persona?.name ||
  [persona?.first_name, persona?.last_name].filter(Boolean).join(" ") ||
  persona?.username ||
  "";

// `discount_percentage` llega como string con 2 decimales («10.00») o `null`
// si el pedido es sin cambio de precio. En la vista, un número (0 = sin %).
const porcentaje = (valor) => Number(valor) || 0;

// La moneda viene como objeto; su código ISO viaja en `symbol` (FC-659).
const codigoMoneda = (currency) =>
  currency && typeof currency === "object" ? currency.symbol || null : currency || null;

const idDe = (valor) => valor?.id ?? valor ?? null;

/** Una solicitud del backend, en la forma que usan las pantallas. */
export function traducirSolicitud(s) {
  if (!s) return null;
  const valorCotizado = Number(s.quoted_amount) || 0;
  return {
    id: s.id,
    // El id de la GESTIÓN (`InteractionManagement`): es el que abre el detalle.
    managementId: idDe(s.itx_management),
    empresa: s.company_crm?.name || "Sin empresa",
    proyecto: s.project_name || "",
    linea: s.business_line?.name || "",
    solicitante: nombreDe(s.user_pbx),
    fecha: s.created_at || null,
    valorCotizado,
    moneda: codigoMoneda(s.currency),
    descuento: porcentaje(s.discount_percentage),
    // Sin porcentaje el backend manda el mismo valor cotizado.
    valorSolicitado: s.resulting_amount != null ? Number(s.resulting_amount) || 0 : valorCotizado,
    comentario: s.comment || "",
    estado: ESTADO_DESDE_BACKEND[s.status] || "pendiente",
    resolvio: nombreDe(s.resolved_by),
    resueltaEn: s.resolved_at || null,
    respuesta: s.response || "",
  };
}

/**
 * El descuento que se aplicó al cerrar como ganada (`won_approval` del detalle,
 * FC-690 CA-03), o null. Lo pinta la interacción ganada.
 */
export function traducirAprobacionGanada(w) {
  if (!w) return null;
  return {
    id: w.id,
    valorCotizado: Number(w.quoted_amount) || 0,
    descuento: porcentaje(w.discount_percentage),
    valorSolicitado: Number(w.resulting_amount) || 0,
    resolvio: nombreDe(w.resolved_by),
    resueltaEn: w.resolved_at || null,
  };
}

/**
 * Una alerta al gestor (`CrmApprovalAlert`, o `datos.alert` del socket
 * `crm_approval_alert`), con la misma forma de vista que una solicitud para que
 * los textos del aviso no distingan de dónde vino. `id` es el de la SOLICITUD.
 * La alerta no trae la moneda: se usa la de la empresa (`monedaPorDefecto`).
 */
export function traducirAlerta(a, monedaPorDefecto = null) {
  if (!a) return null;
  return {
    id: idDe(a.approval),
    alertId: a.id,
    managementId: idDe(a.itx_management),
    empresa: a.company_crm?.name || "Sin empresa",
    proyecto: a.project_name || "",
    contacto: a.contact_name || "",
    moneda: codigoMoneda(a.currency) || monedaPorDefecto,
    descuento: porcentaje(a.discount_percentage),
    valorSolicitado: Number(a.resulting_amount) || 0,
    estado: ESTADO_DESDE_BACKEND[a.result] || "",
    resolvio: nombreDe(a.resolved_by),
    resueltaEn: a.resolved_at || null,
    respuesta: a.response || "",
  };
}

const lista = (data) => (Array.isArray(data) ? data : data?.results || []);

// ---------------------------------------------------------------------------
// Llamadas
// ---------------------------------------------------------------------------

/**
 * Pide una aprobación (FC-686).
 * @param {number|string} managementId - el id de la GESTIÓN.
 * @param {{ discount: number, comment: string }} datos - `discount` en 0 si no
 *   hay porcentaje: viaja como `null` (pedido «sin cambio de precio»).
 * @returns la solicitud creada, en forma de vista.
 */
export async function requestApproval(managementId, { discount, comment }) {
  const pct = Number(discount) || 0;
  const { data } = await apiClient.post(`${MANAGEMENT_URL}${managementId}/request_approval/`, {
    comment: (comment || "").trim(),
    discount_percentage: pct > 0 ? pct : null,
  });
  return traducirSolicitud(data);
}

/**
 * El estado de las aprobaciones de una gestión, desde su detalle
 * (`InteractionManagement/<id>/`, FC-690 CA-02):
 * - `ultima`: `last_approval`, el recuadro de estado y si hay una pendiente;
 * - `aplicada`: la última, si está APROBADA con descuento y la oferta no cambió
 *   (`valorCotizado` = total actual). «Se cierra por» va por su valor (CA-03);
 * - `vencida`: la misma, pero la oferta cambió. Se avisa y se cierra por el
 *   total nuevo (CA-05);
 * - `ganada`: `won_approval`, el descuento que se aplicó al cerrar.
 *
 * Regla del backend: solo cuenta la ÚLTIMA solicitud. Si después de una
 * aprobada se pidió otra y la rechazaron, el descuento ya no aplica.
 * ⚠️ El backend compara ítems y precios; aquí solo se puede comparar el total.
 * El valor que queda de verdad se lee de `won_approval` tras cerrar.
 * @param {object} detalle - `currentInteraction.data`.
 */
export function resumenDeAprobacion(detalle) {
  const ultima = traducirSolicitud(detalle?.last_approval);
  const conDescuento = ultima?.estado === "aprobada" && tieneDescuento(ultima);
  const igual = conDescuento && Number(ultima.valorCotizado) === Number(detalle?.business_amount);
  return {
    ultima,
    pendiente: ultima?.estado === "pendiente",
    aplicada: igual ? ultima : null,
    vencida: conDescuento && !igual ? ultima : null,
    ganada: traducirAprobacionGanada(detalle?.won_approval),
  };
}

/**
 * Las pendientes del alcance del rol, de la más antigua a la más reciente
 * (FC-688). La página las muestra todas en tarjetas: se piden de una vez.
 */
export async function fetchPending({ limit = 1000, offset = 0 } = {}) {
  const { data } = await apiClient.get(`${BASE}pending/`, { params: { limit, offset } });
  return { count: data?.count ?? lista(data).length, items: lista(data).map(traducirSolicitud) };
}

/** Las resueltas, de la más reciente a la más antigua, paginadas (FC-688). */
export async function fetchResolved({ limit = 10, offset = 0 } = {}) {
  const { data } = await apiClient.get(`${BASE}resolved/`, { params: { limit, offset } });
  return { count: data?.count ?? lista(data).length, items: lista(data).map(traducirSolicitud) };
}

/** El número de pendientes del alcance del rol, para el contador del menú. */
export async function fetchPendingCount() {
  const { data } = await apiClient.get(`${BASE}pending_count/`);
  return Number(data?.count) || 0;
}

/** Aprueba una pendiente. La respuesta es opcional: sin ella se manda `{}`. */
export async function approveRequest(id, response) {
  const texto = (response || "").trim();
  const { data } = await apiClient.post(`${BASE}${id}/approve/`, texto ? { response: texto } : {});
  return traducirSolicitud(data);
}

/** Rechaza una pendiente. La respuesta es obligatoria (FC-688 CA-03). */
export async function rejectRequest(id, response) {
  const { data } = await apiClient.post(`${BASE}${id}/reject/`, {
    response: (response || "").trim(),
  });
  return traducirSolicitud(data);
}

/**
 * Traduce un error de este recurso a lo que pintan las pantallas:
 * - `fields`: los mensajes por campo (`comment`, `discount`, `response`);
 * - `detail`: un aviso general (ya hay una pendiente, no está en negocio, ya
 *   estaba resuelta, fuera del grupo…).
 */
export function approvalError(error, fallback = "No se pudo completar la solicitud.") {
  const data = error?.response?.data || {};
  const status = error?.response?.status;
  const fields = {
    comment: [].concat(data.comment || []),
    discount: [].concat(data.discount_percentage || []),
    response: [].concat(data.response || []),
  };
  const hayCampos = Object.values(fields).some((m) => m.length);
  const detail =
    data.detail ||
    (status === 404
      ? "La solicitud ya no existe."
      : status === 403
        ? "No tienes acceso a esta solicitud."
        : hayCampos
          ? ""
          : fallback);
  return { fields, detail };
}
